import { type CardFlavour, CardFlavours } from '@components/Card2/types';
import { useMemo } from 'react';

import type { ValueProvider } from '.cache/__types__';

type UseCardAttributeLock = {
  isLocked: boolean;
  canUnlock: boolean;
  tooltipMessage: string;
};

type UseCardAttributeLockProps = {
  valueLocked: boolean;
  valueProvider: ValueProvider | undefined;
  labelLocked?: boolean;
  labelLockedByTemplate?: boolean;
  flavour: CardFlavour | undefined;
};

/**
 * Use this hook to determine if the padlock icon should show on a card attribute (property/field/image).
 * The padlock only shows if the attribute is fully locked (value and label) or it's shared template field. In this case the edit popover should also not show.
 * Also returns a tooltip message to show on hover.
 */
export function useCardAttributeLock({
  valueLocked,
  valueProvider,
  labelLocked,
  flavour,
}: UseCardAttributeLockProps): UseCardAttributeLock {
  const canUnlock = useMemo((): boolean => {
    if (flavour === CardFlavours.TEMPLATE) {
      return true;
    }

    if (valueProvider === 'template') {
      // This means it's a shared field, shared fields cannot be unlocked outside of the template.
      return false;
    }

    // When in team edit mode, the padlock should only show if valueProvider is template
    if (flavour === CardFlavours.TEAM) {
      return true;
    }

    if (labelLocked === undefined) {
      // labelLocked is undefined when the attribute doesn't have a label (eg. it's a property or image)
      return !valueLocked;
    }

    return !valueLocked || !labelLocked;
  }, [flavour, labelLocked, valueLocked, valueProvider]);

  const tooltipMessage = useMemo(() => {
    return flavour === CardFlavours.TEAM
      ? 'This field has been locked by the template and cannot be edited'
      : 'This field has been locked and cannot be edited';
  }, [flavour]);

  return {
    isLocked: valueLocked, // just bounce back the same value incase it's useful.
    canUnlock,
    tooltipMessage,
  };
}
